import {
   defaultLigandRepresentation,
   defaultPocketRepresentation,
   defaultRestRepresentation,
   Representation,
   StructureComponentType,
} from '../components/molstar/Controls/StructureRepresentationComponent';

/**
 * Generic payload for most `MolstarAction` events.
 *
 * @property rowId         - Identifies the result row being acted on.
 * @property url           - Absolute URL of the structure file (used to derive the
 *                           Molstar structure label via `url.split('/').pop()`).
 * @property value         - Boolean flag whose meaning depends on the action type
 *                           (e.g. `true` = show, `true` = focus).
 * @property focusAfterLoad - When `true`, the structure will be camera-focused once
 *                            it finishes loading (only relevant for `TOGGLE_VISIBILITY`).
 */
export interface MolstarActionPayload {
   rowId: string;
   url: string;
   value: boolean;
   focusAfterLoad?: boolean;
}

/**
 * Payload for representation and/or color change events dispatched via
 * `dispatchRepresentationChange`. A single payload can carry both a representation
 * change and a color change at the same time to reduce the number of Molstar
 * state-tree commits.
 *
 * Tricky fields:
 * - `all`               – when `true`, the change applies globally to every loaded
 *                         structure; `rowId`/`url` are ignored.
 * - `skipControlsSync`  – prevents `AhojTableBase.onRepresentationChanged` from
 *                         updating `controlsMap`. Use this when the table itself is
 *                         the origin of the change and has already updated its state.
 * - `resetTransparency` – after applying the color change, reset alpha to 1.0 for all
 *                         visible structures (used by "reset all colors").
 * - `postAlpha`         – if set, apply this alpha AFTER the color/representation
 *                         update resolves. This prevents `updateRepresentationsTheme`
 *                         from clobbering a separately issued `setStructureTransparency`.
 */
export interface MolstarRepresentationChangePayload {
   type: Representation;
   color: string;
   rowId?: string;
   allStructuresOfSingleType: boolean;
   /** When `true`, the change applies to every loaded structure regardless of type.
    * Does NOT update per-group global color defaults. */
   allStructures?: boolean;
   url: string;
   structureComponentType: StructureComponentType;
   representationChanged: boolean;
   colorChanged: boolean;
   skipControlsSync?: boolean;
   resetTransparency?: boolean;
   /** If set, applied as the final alpha AFTER the color/representation update resolves. */
   postAlpha?: number;
   structureType?: StructureType;
}

/** Extends `MolstarActionPayload` with a single residue's auth_seq_id to focus. */
export interface MolstarResidueFocusPayload extends MolstarActionPayload {
   residueSeqId: number;
}

/**
 * Payload for `SET_TRANSPARENCY` actions.
 * Alpha is in the range [0, 1]; `componentType` optionally restricts the change to
 * a single structural component (pocket, chain, or ligand).
 */
export interface MolstarTransparencyPayload {
   url: string;
   alpha: number;
   componentType?: StructureComponentType;
}

/** Payload for `UNIPROT_HOVER` / `SELECT_UNIPROT_POSITION` actions. `null` clears the highlight. */
export interface MolstarUniprotHoverPayload {
   uniprotPos: number | null;
}

/**
 * Discriminated-union action object dispatched through `StructureEventBusContext.dispatch`.
 * `MolstarComponent` is the sole consumer; it routes each action type to the
 * corresponding `AhojMolstarWrapper` method.
 */
export interface MolstarAction {
   type:
      | 'TOGGLE_VISIBILITY'
      | 'SHOW_ALL'
      | 'HIDE_ALL'
      | 'FOCUS'
      | 'TOGGLE_WATER'
      | 'POCKET_FOCUS'
      | 'RESIDUE_FOCUS'
      | 'SET_TRANSPARENCY'
      | 'UNIPROT_HOVER'
      | 'SELECT_UNIPROT_POSITION';
   payload:
      | MolstarActionPayload
      | MolstarResidueFocusPayload
      | MolstarTransparencyPayload
      | MolstarUniprotHoverPayload
      | null;
}

/**
 * Per-row viewer state stored in `controlsMap`. Every loaded structure row has one
 * `ControlState` entry that drives both the Molstar representation and the table UI.
 *
 * NOTE: Always read/write through `fromControlStateOrDefault` — never mutate the
 * existing object in place, as React needs a new reference to detect the change.
 */
export interface ControlState {
   isVisible: boolean;
   isSelected: boolean;
   selectedRepresentations: Map<StructureComponentType, Representation>;
   pocketColor: string;
   chainColor: string;
   ligandColor: string;
   structureType: StructureType;
   // Index of color assigned from the {@link COLOR_PAIRS} array for quick coloring.
   quickColorIndex?: number;
}

export type StructureType = 'apo' | 'holo' | 'unobserved' | 'query_pocket' | 'alphafold';

/**
 * Maps any specific StructureType to its color-group identifier.
 * `holo`, `unobserved`, and `query_pocket` all share the `'holo'` group.
 */
export const getStructureTypeGroup = (type: StructureType): 'apo' | 'holo' | 'alphafold' => {
   if (type === 'apo') return 'apo';
   if (type === 'alphafold') return 'alphafold';
   return 'holo';
};

/**
 * Returns a fresh `Map` with the default representation assigned to each structural
 * component type. Called during initial load and when resetting row state.
 */
export const getDefaultRepresentation = (): Map<StructureComponentType, Representation> => {
   const map = new Map<StructureComponentType, Representation>();
   map.set('chain', defaultRestRepresentation);
   map.set('pocket', defaultPocketRepresentation);
   map.set('ligand', defaultLigandRepresentation);
   return map;
};

const getDefaultColorByStructureType = (structureType: StructureType): string => {
   if (structureType === 'query_pocket' || structureType === 'holo') {
      return '#f1c40f';
   } else if (structureType === 'alphafold') {
      return '#3498db';
   } else {
      return '#9b59b6';
   }
};

export const getDefaultPocketColor = (structureType: StructureType): string => {
   return getDefaultColorByStructureType(structureType);
};

export const getDefaultChainColor = (structureType: StructureType): string => {
   return getDefaultColorByStructureType(structureType);
};

export const getDefaultLigandColor = (): string => {
   return '#e74c3c';
};

/**
 * Returns a fresh `ControlState` from an existing state entry or, if the entry is
 * `undefined` (row not yet registered), a fully defaulted state object.
 *
 * Always use this helper before mutating row state so that:
 * 1. Missing entries receive sensible defaults instead of throwing.
 * 2. The returned object is a new reference — safe to pass directly to `setControl`.
 */
export const fromControlState = (controlState: ControlState): ControlState => {
   return {
      isVisible: controlState.isVisible,
      isSelected: controlState.isSelected,
      selectedRepresentations: controlState.selectedRepresentations,
      pocketColor: controlState.pocketColor,
      chainColor: controlState.chainColor,
      ligandColor: controlState.ligandColor,
      quickColorIndex: controlState.quickColorIndex,
      structureType: controlState.structureType,
   } as ControlState;
};

export const fromStructureType = (structureType: StructureType): ControlState => {
   return {
      isVisible: false,
      isSelected: false,
      selectedRepresentations: getDefaultRepresentation(),
      pocketColor: getDefaultPocketColor(structureType),
      chainColor: getDefaultChainColor(structureType),
      ligandColor: getDefaultLigandColor(),
      structureType: structureType,
   } as ControlState;
};
